import type { Kernel, MathShim, Params, RNG } from "../sdk/kernel";

interface YieldPartial {
  /** Sum of simulated season yields (t/ha) across this chunk. */
  yieldSum: number;
  /** Count of seasons whose yield fell below the failure threshold. */
  failures: number;
  seasons: number;
}

// Agritech yield sweep — a per-microclimate Monte Carlo of crop yield under weather
// uncertainty. Replaces the earlier agentWalk demo (scoping 2026-06-12): aligns with the
// live agritech segment as a per-microclimate parameter sweep. Each iteration samples a
// season's rainfall and growing-degree-days, runs them through a saturating response
// surface, applies a multiplicative weather-shock, and records the yield. The headline
// result is mean expected yield; per-chunk samples drive the histogram.
//
// Uses math.exp/math.log through the SHIM (lognormal rainfall + Box-Muller temperature
// shock), so it joins the fdlibm verification path at M3.
const RAIN_WIDTH = 260; // mm — tolerance band of the rainfall response (fixed)
const GDD_WIDTH = 600; // tolerance band of the heat response (fixed)
const TWO_PI = 6.283185307179586;

// Gaussian response in [0,1]: 1 at the optimum, falling off with (x−opt)²/width².
function response(x: number, opt: number, width: number, math: MathShim): number {
  const d = (x - opt) / width;
  return math.exp(-(d * d));
}

export const agritech: Kernel<YieldPartial> = {
  id: "agritech",
  label: "Agritech yield sweep",
  // verifiable: true — M3 active. lognormal rainfall + Box-Muller temperature shock use
  // log/cos/exp through the fdlibm WASM shim, which is bitwise-identical across engines
  // (Spike 03), so this kernel reproduces cross-engine.
  verifiable: true,
  blurb: "Per-microclimate crop-yield Monte Carlo under rainfall and temperature uncertainty. Tune the response surface to your own crop and climate.",
  resultUnit: "t/ha (mean yield)",
  defaultN: 500_000,
  chunkSize: 10_000, // ~50 chunks at default N

  params: [
    { key: "baseYield", label: "Potential yield", default: 9.5, min: 1, max: 30, step: 0.5, unit: "t/ha", hint: "Yield under ideal conditions, before weather penalties." },
    { key: "rainOpt", label: "Optimal rainfall", default: 650, min: 100, max: 2000, step: 10, unit: "mm", hint: "Rainfall at which the yield response peaks." },
    { key: "gddOpt", label: "Optimal heat (GDD)", default: 1500, min: 500, max: 4000, step: 50, hint: "Growing-degree-days at which the heat response peaks." },
    { key: "failThreshold", label: "Failure threshold", default: 3, min: 0, max: 10, step: 0.25, unit: "t/ha", hint: "A season below this yield counts as a crop failure." },
  ],

  chunk(count: number, rng: RNG, math: MathShim, params: Params): YieldPartial {
    const BASE_YIELD = params.baseYield!;
    const RAIN_OPT = params.rainOpt!;
    const GDD_OPT = params.gddOpt!;
    const FAILURE_THRESHOLD = params.failThreshold!;
    let yieldSum = 0;
    let failures = 0;
    for (let s = 0; s < count; s++) {
      // Rainfall ~ lognormal(μ,σ) around RAIN_OPT; GDD ~ normal via Box-Muller.
      const u1 = rng.float();
      const u2 = rng.float();
      const zRain = math.sqrt(-2 * math.log(u1 > 0 ? u1 : 1e-12)) * math.cos(TWO_PI * u2);
      const u3 = rng.float();
      const u4 = rng.float();
      const zGdd = math.sqrt(-2 * math.log(u3 > 0 ? u3 : 1e-12)) * math.cos(TWO_PI * u4);

      const rainfall = RAIN_OPT * math.exp(0.18 * zRain); // ~18% lognormal spread
      const gdd = GDD_OPT + 320 * zGdd;

      // Yield = potential × rainfall-response × heat-response × weather shock.
      const shock = 0.9 + 0.2 * rng.float(); // ±10% multiplicative noise
      const y =
        BASE_YIELD *
        response(rainfall, RAIN_OPT, RAIN_WIDTH, math) *
        response(gdd, GDD_OPT, GDD_WIDTH, math) *
        shock;

      yieldSum += y;
      if (y < FAILURE_THRESHOLD) failures++;
    }
    return { yieldSum, failures, seasons: count };
  },

  reduce(partials: YieldPartial[]) {
    let yieldSum = 0;
    let seasons = 0;
    const samples: number[] = [];
    for (const p of partials) {
      yieldSum += p.yieldSum;
      seasons += p.seasons;
      samples.push(p.seasons > 0 ? p.yieldSum / p.seasons : 0); // per-chunk mean yield
    }
    // Final result: mean expected yield (t/ha) across all simulated seasons.
    return { result: seasons > 0 ? yieldSum / seasons : 0, samples };
  },
};
